/**
 * 認証サービス
 * ユーザー認証、トークン管理、セキュリティ対策を提供します
 */

import { userApi } from '../utils/api';
import config from '../config';

// トークンのストレージキー
const TOKEN_KEY = 'authToken';
const USER_KEY = 'currentUser';
const TOKEN_EXPIRY_KEY = 'tokenExpiry';

// トークンの有効期限（ミリ秒）
const TOKEN_LIFETIME = 24 * 60 * 60 * 1000; // 24時間

// 【一時設定】既存ユーザーの有効期限チェックを無効化
// ユーザーが明示的にログアウトするまでトークンを保持
const DISABLE_TOKEN_EXPIRY_CHECK = true;

/**
 * ユーザーログイン処理
 * @param {string} loginId - ユーザー名またはメールアドレス
 * @param {string} password - ユーザーパスワード
 * @returns {Promise<object>} - ログイン結果とユーザー情報
 */
export const login = async (loginId, password) => {
  console.log('【デバッグ】authService.login開始:', { loginId });
  
  if (!loginId || !password) {
    const error = 'ユーザー名/メールアドレスとパスワードを入力してください';
    console.log('【デバッグ】authService.login - バリデーションエラー:', error);
    return { success: false, error };
  }
  
  try {
    // ログインAPIにリクエストを送信
    console.log('【デバッグ】authService.login - API呼び出し開始');
    const response = await userApi.login(loginId, password);
    console.log('【デバッグ】authService.login - APIレスポンス:', response);
    
    // ログイン成功の条件をより柔軟に設定
    const isSuccess = 
      (response && response.success === true && response.access_token) ||
      (response && response.message && response.access_token) ||
      (response && response.message && response.message.includes('ログイン成功')) ||
      (response && response.message && response.message.includes('登録が完了'));
    
    console.log('【デバッグ】authService.login - 成功判定:', { 
      isSuccess,
      hasAccessToken: !!response.access_token,
      hasMessage: !!response.message,
      message: response.message
    });
    
    if (isSuccess) {
      console.log('【デバッグ】authService.login - ログイン成功処理開始');
      
      // トークンと有効期限を保存
      const accessToken = response.access_token;
      const refreshToken = response.refresh_token || response.refreshToken;
      const user = response.user;
      
      // トークン有効期限を設定（デフォルト24時間）
      const tokenExpiry = Date.now() + TOKEN_LIFETIME;
      
      // localStorageに保存
      localStorage.setItem(TOKEN_KEY, accessToken);
      localStorage.setItem(TOKEN_EXPIRY_KEY, tokenExpiry.toString());
      
      // sessionStorageにも併用して保存（より確実な取得のため）
      sessionStorage.setItem(TOKEN_KEY, accessToken);
      sessionStorage.setItem(TOKEN_EXPIRY_KEY, tokenExpiry.toString());
      
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
        sessionStorage.setItem('refreshToken', refreshToken);
      }
      
      if (user) {
        localStorage.setItem(USER_KEY, JSON.stringify(user));
        sessionStorage.setItem(USER_KEY, JSON.stringify(user));
        console.log('【デバッグ】authService.login - ユーザー情報保存完了:', user);
      }
      
      console.log('【デバッグ】authService.login - トークン保存完了:', {
        tokenSaved: !!localStorage.getItem(TOKEN_KEY),
        sessionTokenSaved: !!sessionStorage.getItem(TOKEN_KEY),
        userSaved: !!localStorage.getItem(USER_KEY),
        refreshTokenSaved: !!localStorage.getItem('refreshToken'),
        tokenExpiry: localStorage.getItem(TOKEN_EXPIRY_KEY)
      });
      
      console.log('【デバッグ】authService.login - ログイン成功');
      return { 
        success: true, 
        user, 
        access_token: accessToken,
        refresh_token: refreshToken,
        message: response.message || 'ログインが完了しました'
      };
    } else {
      console.log('【デバッグ】authService.login - ログイン失敗:', response);
      const errorMessage = response.error || response.message || 'ログインに失敗しました';
      console.log('【デバッグ】authService.login - エラーメッセージ:', errorMessage);
      return { 
        success: false, 
        error: errorMessage
      };
    }
  } catch (error) {
    console.error('【デバッグ】authService.login - エラー:', error);
    
    let errorMessage = 'ログイン処理中にエラーが発生しました';
    
    // エラーの詳細を調べて適切なメッセージを返す
    if (error.response) {
      // HTTPレスポンスエラー
      const status = error.response.status;
      const data = error.response.data;
      
      console.log('【デバッグ】authService.login - HTTPエラー:', { status, data });
      
      if (status === 401) {
        errorMessage = data?.error || 'ユーザー名/メールアドレスまたはパスワードが正しくありません';
      } else if (status === 403) {
        errorMessage = data?.error || 'アカウントがロックされています';
      } else if (status === 400) {
        errorMessage = data?.error || 'リクエストが無効です';
      } else if (status >= 500) {
        errorMessage = 'サーバーでエラーが発生しました。しばらくしてから再試行してください';
      } else {
        errorMessage = data?.error || data?.message || `サーバーエラー (${status})`;
      }
    } else if (error.request) {
      // ネットワークエラー
      console.log('【デバッグ】authService.login - ネットワークエラー:', error.request);
      errorMessage = 'ネットワークエラーが発生しました。インターネット接続を確認してください';
    } else {
      // その他のエラー
      console.log('【デバッグ】authService.login - その他のエラー:', error.message);
      errorMessage = error.message || 'ログイン処理中にエラーが発生しました';
    }
    
    console.log('【デバッグ】authService.login - 最終エラーメッセージ:', errorMessage);
    return { 
      success: false, 
      error: errorMessage
    };
  }
};

/**
 * ユーザー登録処理
 * @param {object} userData - ユーザー登録データ
 * @returns {Promise<object>} - 登録結果とユーザー情報
 */
export const register = async (userData) => {
  try {
    const response = await userApi.register(userData);
    
    console.log('authService.register response:', response);
    
    // バックエンドのレスポンス形式に合わせて成功判定を修正
    const isSuccess = response && (
      response.success === true ||
      (response.message && response.access_token) ||
      (response.message && response.message.includes('登録が完了'))
    );
    
    if (isSuccess) {
      return { 
        success: true, 
        user: response.user,
        message: response.message,
        access_token: response.access_token,
        refresh_token: response.refresh_token
      };
    } else {
      throw new Error(response.message || response.error || 'ユーザー登録に失敗しました');
    }
  } catch (error) {
    console.error('authService.register error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * ユーザーログアウト処理
 */
export const logout = () => {
  console.log('【デバッグ】authService.logout実行');
  
  // localStorageとsessionStorage両方をクリア
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
  localStorage.removeItem('refreshToken');
  
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(USER_KEY);
  sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
  sessionStorage.removeItem('refreshToken');
  
  // 古いキー名もクリア（互換性のため）
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('user');
  
  console.log('【デバッグ】authService.logout完了 - 全ての認証情報をクリア');
};

/**
 * 保存されたトークンを取得
 * @returns {string|null} - アクセストークン、なければnull
 */
export const getToken = () => {
  try {
    // まずlocalStorageから取得を試行
    let token = localStorage.getItem(TOKEN_KEY);
    
    // localStorageにない場合はsessionStorageから取得
    if (!token) {
      token = sessionStorage.getItem(TOKEN_KEY);
      console.log('【デバッグ】getToken - sessionStorageから取得:', !!token);
    }
    
    if (!token) {
      console.log('【デバッグ】getToken - トークンなし');
      return null;
    }
    
    // トークンの有効期限をチェック（設定で無効化可能）
    if (!DISABLE_TOKEN_EXPIRY_CHECK) {
      const tokenExpiry = localStorage.getItem(TOKEN_EXPIRY_KEY) || sessionStorage.getItem(TOKEN_EXPIRY_KEY);
      if (tokenExpiry && parseInt(tokenExpiry) < Date.now()) {
        console.log('【デバッグ】getToken - トークンが期限切れ、ログアウト実行');
        logout();
        return null;
      }
    }
    
    console.log('【デバッグ】getToken - トークン取得成功');
    return token;
  } catch (error) {
    console.error('【デバッグ】getToken - エラー:', error);
    return null;
  }
};

/**
 * 現在のユーザー情報を取得
 * @returns {object|null} - ログイン中のユーザー情報、未ログインならnull
 */
export const getCurrentUser = () => {
  try {
    const token = getToken(); // 統一されたgetToken関数を使用
    
    // まずlocalStorageから取得を試行
    let userJson = localStorage.getItem(USER_KEY);
    
    // localStorageにない場合はsessionStorageから取得
    if (!userJson) {
      userJson = sessionStorage.getItem(USER_KEY);
      console.log('【デバッグ】getCurrentUser - sessionStorageからユーザー情報取得:', !!userJson);
    }
    
    const tokenExpiry = localStorage.getItem(TOKEN_EXPIRY_KEY) || sessionStorage.getItem(TOKEN_EXPIRY_KEY);
    
    console.log('【デバッグ】getCurrentUser - トークン状態:', { 
      hasToken: !!token,
      hasUser: !!userJson,
      tokenExpiry: tokenExpiry,
      currentTime: Date.now(),
      expiryTime: tokenExpiry ? parseInt(tokenExpiry) : null,
      isExpired: tokenExpiry && parseInt(tokenExpiry) < Date.now(),
      disableExpiryCheck: DISABLE_TOKEN_EXPIRY_CHECK
    });
    
    if (!token || !userJson) {
      console.log('【デバッグ】getCurrentUser - トークンまたはユーザー情報なし');
      return null;
    }
    
    const user = JSON.parse(userJson);
    console.log('【デバッグ】getCurrentUser - ユーザー情報取得成功:', user);
    return user;
  } catch (error) {
    console.error('【デバッグ】getCurrentUser - エラー:', error);
    return null;
  }
};

/**
 * ユーザーがログイン中かどうかを確認
 * @returns {boolean} - ログイン状態
 */
export const isLoggedIn = () => {
  return !!getToken();
};

/**
 * ユーザーが特定の権限を持っているかチェック
 * @param {string} role - 確認する権限（admin, corporate, poster, viewer）
 * @returns {boolean} - 権限を持っているかどうか
 */
export const hasRole = (role) => {
  const user = getCurrentUser();
  return user && user.role === role;
};

/**
 * ユーザーが指定された複数の権限のいずれかを持っているかチェック
 * @param {string[]} roles - 確認する権限の配列
 * @returns {boolean} - いずれかの権限を持っているかどうか
 */
export const hasAnyRole = (roles) => {
  const user = getCurrentUser();
  return user && roles.includes(user.role);
};

/**
 * トークンの更新処理
 * @returns {Promise<boolean>} - 更新が成功したかどうか
 */
export const refreshToken = async () => {
  try {
    const token = getToken();
    
    if (!token) {
      return false;
    }
    
    // トークン更新APIを呼び出し
    const response = await userApi.refreshToken();
    
    if (response.token) {
      // 新しいトークンと有効期限を保存
      const expiryTime = Date.now() + TOKEN_LIFETIME;
      
      localStorage.setItem(TOKEN_KEY, response.token);
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
      
      return true;
    } else {
      throw new Error(response.message || 'トークンの更新に失敗しました');
    }
  } catch (error) {
    
    
    // エラーの場合はログアウト
    logout();
    return false;
  }
};

/**
 * 【デバッグ用】認証状態のチェック
 * コンソールから直接実行可能
 */
export const debugAuthState = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  const user = localStorage.getItem(USER_KEY);
  const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
  
  const state = {
    hasToken: !!token,
    tokenLength: token ? token.length : 0,
    hasUser: !!user,
    user: user ? JSON.parse(user) : null,
    tokenExpiry: expiry,
    expiryTime: expiry ? parseInt(expiry) : null,
    currentTime: Date.now(),
    isExpired: expiry && parseInt(expiry) < Date.now(),
    disableExpiryCheck: DISABLE_TOKEN_EXPIRY_CHECK
  };
  
  console.log('【デバッグ】認証状態:', state);
  return state;
};

// 認証サービスをエクスポート
export default {
  login,
  register,
  logout,
  getCurrentUser,
  getToken,
  isLoggedIn,
  hasRole,
  hasAnyRole,
  refreshToken,
  debugAuthState
}; 